import { groupInviteGetInviteDetails } from '@/api/gen-new/group/group';
import { prefetchSignedInShell } from '@/app/prefetchSignedInShell';
import AuthFormLayout from '@/auth/components/AuthFormLayout';
import { BiometricSignInButton } from '@/auth/biometric/BiometricSignInButton';
import { getEnrollment } from '@/auth/biometric/biometricEnrollment';
import GoogleLoginButton from '@/auth/components/GoogleLoginButton';
import { TwoFactorSignInForm } from '@/auth/components/TwoFactorSignInForm';
import { routeAfterAuthenticated } from '@/auth/constants/authSettings';
import { getSession } from '@/auth/AuthSession';
import { AuthenticationError, InvitationError } from '@/auth/Errors';
import useAuth from '@/auth/useAuth';
import defaultNotifications from '@/components/functions/defaultNotifications';
import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Button, Group, Loader, PasswordInput, Text, TextInput } from '@mantine/core';
import { IconAlertCircle, IconUsers } from '@tabler/icons-react';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router';
import * as yup from 'yup';

let draftEmail: string | undefined; // module-scope email draft (no password persistence)
const INVALID_CREDENTIALS_KEY = 'login.notifications.invalidCredentials';
const USER_NOT_VERIFIED_KEY = 'login.notifications.userNotVerified';
const INVALID_INVITATION_KEY = 'login.notifications.invalidInvitation';
const NETWORK_ERROR_KEY = 'login.notifications.networkError';

const schema = yup.object({
	email: yup.string().trim().lowercase().email().required(),
	password: yup.string().required(),
});

type FormData = yup.InferType<typeof schema>;

export type LoginLocationState = {
	showNotification?: boolean;
	notificationTitle?: string;
	notificationMessage?: string;
	notificationType?: 'success' | 'error';
};

function getOriginalError(error: unknown): unknown {
	return error instanceof AuthenticationError ? error.originalError : error;
}

function isNetworkError(error: unknown): boolean {
	const originalError = getOriginalError(error);

	return isAxiosError(originalError) && !originalError.response;
}

function logAuthError(error: unknown): void {
	const originalError = getOriginalError(error);

	if (!isAxiosError(originalError)) {
		console.warn('Email sign-in failed:', originalError);
		return;
	}

	console.warn('Email sign-in failed:', {
		baseURL: originalError.config?.baseURL,
		code: originalError.code,
		message: originalError.message,
		responseMessage: (originalError.response?.data as { message?: unknown } | undefined)?.message,
		status: originalError.response?.status,
		url: originalError.config?.url,
	});
}

export default function LoginPage() {
	const navigate = useNavigate();
	const location = useLocation();
	const { state: locationState }: { state: LoginLocationState } = location as any;
	const searchParams = new URLSearchParams(location.search);
	const redirectPath = searchParams.get('r') || '/';
	const invitationCode = searchParams.get('code');

	const { signIn, isLoading: authIsLoading, isAuthenticated } = useAuth();

	const [invitationDetails, setInvitationDetails] = useState<any>(null);
	const [invitationError, setInvitationError] = useState<string | null>(null);
	// The router navigates in a transition, which keeps this page on screen until the next one
	// has loaded; stay busy until then so the sign-in does not look like it silently failed.
	const [isRedirecting, setIsRedirecting] = useState(false);
	const [showBiometricSignIn, setShowBiometricSignIn] = useState(() => getEnrollment() !== null);
	const [twoFactorChallenge, setTwoFactorChallenge] = useState<{ token: string; email: string } | null>(null);

	// Redirect already authenticated users to the appropriate page
	useEffect(() => {
		if (isAuthenticated()) {
			const session = getSession();
			const redirectPath =
				session && session.isLoggedIn && !session.userGroupId ? '/no-space' : routeAfterAuthenticated;
			navigate(redirectPath, { replace: true });
		} else {
			prefetchSignedInShell();
		}
	}, [isAuthenticated, navigate, redirectPath]);

	const {
		register,
		handleSubmit,
		setError,
		watch,
		formState: { errors, isSubmitting },
		setValue: setFormValue,
	} = useForm<FormData>({
		resolver: yupResolver(schema),
		defaultValues: { email: draftEmail ?? '' },
	});
	const isBusy = isSubmitting || authIsLoading || isRedirecting;

	// Persist email draft across component remounts (do not persist password for security)
	useEffect(() => {
		const sub = watch(v => {
			if (v.email !== undefined) {
				draftEmail = v.email;
			}
		});
		return () => sub.unsubscribe();
	}, [watch]);

	useEffect(() => {
		if (locationState?.showNotification) {
			const showNotification =
				locationState.notificationType === 'error' ? defaultNotifications.showError : defaultNotifications.showSuccess;
			showNotification({
				title: locationState.notificationTitle ?? '',
				// biome-ignore lint/style/noNonNullAssertion: FIXME
				message: locationState.notificationMessage!,
			});
		}
	}, [locationState]);

	// Fetch invitation details if invitation code is present
	useEffect(() => {
		if (invitationCode) {
			const fetchInvitationDetails = async () => {
				try {
					const details = await groupInviteGetInviteDetails(invitationCode);
					setInvitationDetails(details);
					setInvitationError(null); // Clear any previous invitation errors

					// Pre-fill email from invitation details
					if (details.email) {
						setFormValue('email', details.email);
					}
				} catch (error) {
					console.error('Failed to fetch invitation details:', error);
					setInvitationError(t(INVALID_INVITATION_KEY));
				}
			};
			fetchInvitationDetails();
		}
	}, [invitationCode, setFormValue]);

	const navigateAfterSignIn = () => {
		setIsRedirecting(true);

		// Check if user has a group after successful login
		const session = getSession();
		if (session && session.isLoggedIn && !session.userGroupId) {
			// User has no group, redirect to no-space page
			navigate('/no-space');
		} else {
			// User has a group or is in invitation flow, redirect normally
			navigate(redirectPath.length > 1 ? redirectPath : routeAfterAuthenticated);
		}
	};

	const onSubmit = async ({ email, password }: FormData) => {
		// Clear any previous invitation errors
		setInvitationError(null);

		try {
			const { twoFactorToken } = await signIn(email, password, invitationCode || undefined);
			if (twoFactorToken) {
				setTwoFactorChallenge({ token: twoFactorToken, email });
				return;
			}
			navigateAfterSignIn();
		} catch (error) {
			logAuthError(error);

			if (error instanceof InvitationError) {
				// Invitation error - show in the Alert component
				setInvitationError(t(INVALID_INVITATION_KEY));
			} else if (error instanceof AuthenticationError && error.type === 'USER_NOT_VERIFIED') {
				// User not verified - show notification instead of form error
				defaultNotifications.showError({
					message: t(USER_NOT_VERIFIED_KEY),
				});
			} else if (isNetworkError(error)) {
				defaultNotifications.showError({
					message: t(NETWORK_ERROR_KEY),
				});
			} else {
				// Authentication error (invalid credentials) - show credential error message
				// URL is preserved automatically, no need to redirect
				setError('password', {
					type: 'manual',
					message: t(INVALID_CREDENTIALS_KEY),
				});
			}
		}
	};

	// Create invitation header for both success and error states
	const invitationHeader = invitationCode ? (
		invitationError ? (
			// Show invitation error
			<Alert icon={<IconAlertCircle size={16} />} title={t('login.invitationError')} color="red" variant="light">
				<Text size="sm">{invitationError}</Text>
			</Alert>
		) : invitationDetails ? (
			// Show successful invitation details
			<Alert icon={<IconUsers size={16} />} title={t('login.joinSpace')} color="blue" variant="light">
				<Text size="sm">
					{t('login.inviteMessage', {
						spaceName: invitationDetails.userGroup?.name || 'this space',
					})}
				</Text>
			</Alert>
		) : null
	) : showBiometricSignIn ? (
		<BiometricSignInButton
			// SecureRoute adds `r` when a session lapses; a deliberate sign-out lands here without it.
			autoPrompt={searchParams.has('r')}
			disabled={isBusy}
			onSignedIn={navigateAfterSignIn}
			onHide={() => setShowBiometricSignIn(false)}
		/>
	) : null;

	if (twoFactorChallenge) {
		return (
			<TwoFactorSignInForm
				twoFactorToken={twoFactorChallenge.token}
				email={twoFactorChallenge.email}
				onSignedIn={navigateAfterSignIn}
				onCancel={() => setTwoFactorChallenge(null)}
			/>
		);
	}

	return (
		<AuthFormLayout
			title={t('login.title')}
			header={invitationHeader}
			onSubmit={handleSubmit(onSubmit)}
			fields={
				<>
					<TextInput
						{...register('email')}
						label={t('login.email')}
						error={errors.email?.message}
						disabled={isBusy || !!invitationDetails?.email}
						autoCapitalize="none"
						autoComplete="username"
						autoCorrect="off"
						inputMode="email"
						spellCheck={false}
						type="email"
					/>
					<PasswordInput
						{...register('password')}
						label={t('login.password')}
						autoComplete="current-password"
						error={errors.password?.message}
						disabled={isBusy}
					/>
					{(authIsLoading || isRedirecting) && (
						<Group justify="center" mt="sm">
							<Loader size="sm" />
						</Group>
					)}
				</>
			}
			auxLink={{
				text: t('login.forgotPassword'),
				to: '/forgot-password',
			}}
			actionButton={
				<Button type="submit" aria-disabled={isBusy} disabled={isBusy} variant="filled">
					{t('login.login')}
				</Button>
			}
			altAction={{
				preText: t('login.registerText'),
				text: t('login.register'),
				to: `/register?r=${redirectPath}${invitationCode ? `&code=${invitationCode}` : ''}`,
			}}
			footerDividerText={t('login.or')}
			footer={
				<Group justify="center">
					<GoogleLoginButton />
				</Group>
			}
		/>
	);
}
